import type { OperatorCallView, OperatorProfile } from '@360face/domain';
import { ApiError } from '../api/client';

/** Operator console client (owner 20.10.2026). Session token in sessionStorage, 12 h. */
export interface OperatorApi {
  readonly loggedIn: boolean;
  login(email: string, pin: string): Promise<void>;
  logout(): void;
  me(): Promise<OperatorProfile>;
  setOnline(online: boolean): Promise<OperatorProfile>;
  /** Polling it also tells the server the console is open (online for 30 s). */
  calls(online: boolean): Promise<OperatorCallView[]>;
  answer(id: string): Promise<OperatorCallView>;
  extend(id: string): Promise<OperatorCallView>;
  callTherapist(id: string): Promise<void>;
  end(id: string): Promise<void>;
}

const KEY = 'hub-operator-session';

function store(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function createOperatorApi(baseUrl = '', fetchImpl: typeof fetch = (...a) => fetch(...a)): OperatorApi {
  let token: string | undefined = store()?.getItem(KEY) ?? undefined;
  const save = (v?: string) => {
    token = v;
    try {
      if (v) store()?.setItem(KEY, v);
      else store()?.removeItem(KEY);
    } catch {
      // private mode: the session lives until reload
    }
  };

  async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
    let res: Response;
    try {
      res = await fetchImpl(`${baseUrl}${path}`, {
        method,
        headers: {
          Accept: 'application/json',
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: AbortSignal.timeout(20_000),
      });
    } catch {
      throw new ApiError(0, 'NETWORK', true);
    }
    const json = (await res.json().catch(() => ({}))) as {
      data?: T;
      error?: { code?: string };
      message?: { error?: { code?: string } };
    };
    if (!res.ok) {
      const code = json.error?.code ?? json.message?.error?.code ?? 'UNKNOWN';
      if (res.status === 401 && path !== '/v1/operator/session') save(undefined);
      throw new ApiError(res.status, code, res.status >= 500);
    }
    return json.data as T;
  }

  return {
    get loggedIn() {
      return Boolean(token);
    },
    login: async (email, pin) => {
      const r = await call<{ token: string }>('POST', '/v1/operator/session', { email, pin });
      save(r.token);
    },
    logout: () => save(undefined),
    me: () => call('GET', '/v1/operator/me'),
    setOnline: (online) => call('POST', '/v1/operator/presence', { online }),
    calls: (online) => call('GET', `/v1/operator/calls?online=${online ? 1 : 0}`),
    answer: (id) => call('POST', `/v1/operator/calls/${encodeURIComponent(id)}/answer`),
    extend: (id) => call('POST', `/v1/operator/calls/${encodeURIComponent(id)}/extend`),
    callTherapist: async (id) => {
      await call('POST', `/v1/operator/calls/${encodeURIComponent(id)}/therapist`);
    },
    end: async (id) => {
      await call('POST', `/v1/operator/calls/${encodeURIComponent(id)}/end`);
    },
  };
}
